import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import './Arrived.css';

interface CamperArrival {
  id: string;
  firstName: string;
  lastName: string;
  placeName: string;
  arrived: boolean;
  accommodationType: string;
  startDate: string;
  endDate: string;
}

const Arrived: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [campers, setCampers] = useState<CamperArrival[]>([]);
  const [showArrived, setShowArrived] = useState(true);
  const [totalSites, setTotalSites] = useState(0);
  const [arrivedCount, setArrivedCount] = useState(0);

  useEffect(() => {
    const fetchArrivalData = async () => {
      try {
        setLoading(true);
        setError(null);

        const registrationsRef = collection(db, 'Registrations');
        const snapshot = await getDocs(registrationsRef);
        
        const camperData: CamperArrival[] = [];
        
        snapshot.forEach(doc => {
          const data = doc.data();
          
          // Only include campers with assigned accommodations
          const assignedAccommodation = data.assignedAccommodation || data.accommodationFees;
          const placeCode = data.financial?.accommodationFees?.assignedAccommodation || assignedAccommodation?.placeCode;
          
          if (placeCode) {
            // Extract name from different possible structures
            const primaryCamperName = data.registrationInfo?.primaryCamperName || '';
            const nameParts = primaryCamperName.split(' ');
            const firstName = nameParts[0] || data.firstName || '';
            const lastName = nameParts.slice(1).join(' ') || data.lastName || '';
            
            camperData.push({
              id: doc.id,
              firstName,
              lastName,
              placeName: assignedAccommodation?.placeName || data.financial?.accommodationFees?.placeName || placeCode,
              arrived: data.Arrived || false,
              accommodationType: data.accommodationType || '',
              startDate: data.startDate || data.stayDetails?.startDate || data.selectedDates?.startDate || data.campDates?.arrivalDate || '',
              endDate: data.endDate || data.stayDetails?.endDate || data.selectedDates?.endDate || data.campDates?.departureDate || ''
            });
          }
        });

        setCampers(camperData);
        
        // Calculate totals
        const total = camperData.length;
        const arrived = camperData.filter(camper => camper.arrived).length;
        
        setTotalSites(total);
        setArrivedCount(arrived);
        
      } catch (err) {
        console.error('Error fetching arrival data:', err);
        setError('Failed to load arrival data');
      } finally {
        setLoading(false);
      }
    };

    fetchArrivalData();
  }, []);

  const handleArrivalToggle = async (camperId: string, newArrivedStatus: boolean) => {
    try {
      // Update local state immediately for responsive UI
      setCampers(prevCampers => 
        prevCampers.map(camper => 
          camper.id === camperId 
            ? { ...camper, arrived: newArrivedStatus }
            : camper
        )
      );

      // Update Firestore
      const docRef = doc(db, 'Registrations', camperId);
      await updateDoc(docRef, {
        Arrived: newArrivedStatus
      });

      // Recalculate totals
      const updatedCampers = campers.map(camper => 
        camper.id === camperId 
          ? { ...camper, arrived: newArrivedStatus }
          : camper
      );
      
      const newArrivedCount = updatedCampers.filter(camper => camper.arrived).length;
      setArrivedCount(newArrivedCount);
      
    } catch (err) {
      console.error('Error updating arrival status:', err);
      // Revert local state on error
      setCampers(prevCampers => 
        prevCampers.map(camper => 
          camper.id === camperId 
            ? { ...camper, arrived: !newArrivedStatus }
            : camper
        )
      );
    }
  };

  const filteredCampers = campers.filter(camper => 
    showArrived ? camper.arrived : !camper.arrived
  );

  const exportToCSV = () => {
    // Create CSV header
    const headers = ['Name', 'Place Name', 'Arrived', 'Accommodation Type', 'Start Date', 'End Date'];
    
    // Create CSV rows
    const csvRows = [
      headers.join(','),
      ...filteredCampers.map(camper => [
        `"${camper.firstName} ${camper.lastName}"`,
        `"${camper.placeName}"`,
        camper.arrived ? 'Yes' : 'No',
        `"${camper.accommodationType}"`,
        `"${camper.startDate}"`,
        `"${camper.endDate}"`
      ].join(','))
    ];
    
    const csvContent = csvRows.join('\n');
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `arrived-report-${showArrived ? 'arrived' : 'not-arrived'}-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return <div className="arrived-report loading">Loading arrival data...</div>;
  }

  if (error) {
    return <div className="arrived-report error">{error}</div>;
  }

  return (
    <div className="arrived-report">
      <h2>Arrival Report</h2>
      
      <div className="summary-section">
        <div className="summary-stats">
          <span className="total-sites">Total Sites: {totalSites}</span>
          <span className="arrived-count">Arrived: {arrivedCount}</span>
        </div>
        
        <div className="view-toggle">
          <button 
            className={`toggle-btn ${showArrived ? 'active' : ''}`}
            onClick={() => setShowArrived(true)}
          >
            Arrived
          </button>
          <button 
            className={`toggle-btn ${!showArrived ? 'active' : ''}`}
            onClick={() => setShowArrived(false)}
          >
            Not Arrived
          </button>
        </div>
      </div>

      <div className="campers-section">
        <div className="section-header">
          <h3>{showArrived ? 'Arrived Campers' : 'Not Yet Arrived'}</h3>
          {filteredCampers.length > 0 && (
            <button 
              className="export-csv-btn"
              onClick={exportToCSV}
            >
              Export to CSV
            </button>
          )}
        </div>
        
        {filteredCampers.length === 0 ? (
          <div className="no-campers">
            {showArrived ? 'No campers have arrived yet.' : 'All campers have arrived.'}
          </div>
        ) : (
          <table className="campers-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Place Name</th>
                <th>Arrived</th>
              </tr>
            </thead>
            <tbody>
              {filteredCampers.map(camper => (
                <tr key={camper.id}>
                  <td className="camper-name">
                    {camper.firstName} {camper.lastName}
                  </td>
                  <td className="place-name">
                    {camper.placeName}
                  </td>
                  <td className="arrived-checkbox">
                    <input
                      type="checkbox"
                      checked={camper.arrived}
                      onChange={(e) => handleArrivalToggle(camper.id, e.target.checked)}
                      className="arrival-checkbox"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Arrived; 